/**
 * PlaybookSaveBar — sticky save bar for the playbook page.
 *   clean: "All changes saved" + disabled Save changes
 *   dirty: "N unsaved change(s)" + Discard + Save changes
 * A `statusMessage` (e.g. the post-save confirmation) replaces the clean label as a `role=status`.
 */

import { Button } from '../../ui/button';

export interface PlaybookSaveBarProps {
  changeCount: number;
  saving: boolean;
  statusMessage: string | null;
  onSave: () => void;
  onDiscard: () => void;
}

export function PlaybookSaveBar({ changeCount, saving, statusMessage, onSave, onDiscard }: PlaybookSaveBarProps) {
  const dirty = changeCount > 0;
  return (
    <>
      {/* The app shell's <main> is a scroll container, so `sticky` cannot pin the bar: it is fixed
          to the viewport instead (above the mobile tab bar, beside the desktop sidebar/rail). */}
      <div
        data-testid="playbook-save-bar"
        className="fixed inset-x-0 bottom-[calc(var(--bottom-tab-height)+env(safe-area-inset-bottom,0px))] z-30 border-t border-border bg-background md:bottom-0 md:left-[var(--sidebar-width)] lg:right-[var(--workflow-rail-width,3rem)]"
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-end gap-2 px-[var(--page-padding-x)] py-3">
          {dirty ? (
            <span className="mr-auto text-sm font-semibold text-warning" data-testid="playbook-unsaved-count">
              {changeCount === 1 ? '1 unsaved change' : `${changeCount} unsaved changes`}
            </span>
          ) : statusMessage ? (
            <span role="status" className="mr-auto text-sm font-semibold text-success" data-testid="playbook-save-status">
              {statusMessage}
            </span>
          ) : (
            <span className="mr-auto text-sm text-muted-foreground" data-testid="playbook-clean-label">
              All changes saved
            </span>
          )}
          {dirty && (
            <Button type="button" variant="ghost" onClick={onDiscard} disabled={saving} data-testid="playbook-discard-button">
              Discard
            </Button>
          )}
          <Button type="button" onClick={onSave} disabled={!dirty || saving} data-testid="playbook-save-button">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
      {/* Spacer so the last section is never hidden behind the fixed bar */}
      <div className="h-16" aria-hidden="true" />
    </>
  );
}

export default PlaybookSaveBar;
